import { useMemo } from 'react';
import { ValidationError } from 'yup';
import { useMutation } from '@apollo/client';
import * as Sentry from '@sentry/browser';
import { ExternalReference } from 'graphql-query-contracts';
import {
  useExternalReferenceItems,
  filterValidExternalReferences,
} from 'components/externalReferences';
import {
  addPropertyExternalReferences,
  removePropertyExternalReferences,
} from 'graphql-queries';
import { ExternalReferenceCacheHandler } from '../cache';
import { Channel } from 'lane-shared/types/ChannelType';

export const useEditPropertyExternalReferences = ({
  references,
  channel,
}: {
  references: ExternalReference[];
  channel: Channel;
}) => {
  const propertyId = channel._id;
  const cacheHandler = useMemo(
    () => new ExternalReferenceCacheHandler(propertyId),
    [propertyId]
  );

  const { referenceItems, add, edit, remove, validate } =
    useExternalReferenceItems(references);

  const [removeReferencesMutation] = useMutation(
    removePropertyExternalReferences
  );
  const [addReferencesMutation] = useMutation(addPropertyExternalReferences);

  const handleRemoveReferences = async (
    referencesToRemove: ExternalReference[]
  ) => {
    if (referencesToRemove.length === 0) return;

    const failedReferences: ExternalReference[] = [];

    try {
      const { data } = await removeReferencesMutation({
        variables: {
          channelId: propertyId,
          externalReferences: referencesToRemove.map(ref => ({
            source: {
              sourceEntityId: ref.source.sourceEntityId,
              sourceEntityType: ref.source.sourceEntityType,
              sourceType: ref.source.sourceType,
            },
            relationshipSource: ref.relationshipSource
              ? {
                  sourceType: ref.relationshipSource?.sourceType,
                  sourceRelationshipType:
                    ref.relationshipSource?.sourceRelationshipType,
                  sourceRelationshipId:
                    ref.relationshipSource?.sourceRelationshipId,
                }
              : undefined,
          })),
        },
        update: (cache, { data }) => {
          if (data?.removePropertyExternalReferences?.successful?.length) {
            cacheHandler.removeReferences(
              data.removePropertyExternalReferences.successful
            );
          }
        },
      });

      if (data?.removePropertyExternalReferences?.failed?.length) {
        failedReferences.push(...data.removePropertyExternalReferences.failed);
        const err = new Error('Some references failed to be removed');

        throw err;
      }
    } catch (error) {
      Sentry.captureException(error, {
        contexts: {
          params: {
            propertyId,
            referencesToRemove,
            failedReferences,
          },
        },
      });

      throw error;
    }
  };

  const handleAddReferences = async (referencesToAdd: ExternalReference[]) => {
    if (referencesToAdd.length === 0) return;

    const failedReferences: ExternalReference[] = [];

    try {
      const { data } = await addReferencesMutation({
        variables: {
          channel: { _id: propertyId, name: channel.name },
          externalReferences: referencesToAdd.map(ref => ({
            sourceEntityId: ref.source.sourceEntityId,
            sourceType: ref.source.sourceType,
          })),
        },
        update: (cache, { data }) => {
          if (data?.addPropertyExternalReferences?.successful?.length) {
            cacheHandler.addReferences(
              data.addPropertyExternalReferences.successful
            );
          }
        },
      });

      if (data?.addPropertyExternalReferences?.failed?.length) {
        failedReferences.push(...data.addPropertyExternalReferences.failed);
        const err = new Error('Some references failed to be added');

        throw err;
      }
    } catch (error) {
      Sentry.captureException(error, {
        contexts: {
          params: {
            propertyId,
            referencesToAdd,
            failedReferences,
          },
        },
      });

      throw error;
    }
  };

  const getCurrentReferences = (): ExternalReference[] => {
    return filterValidExternalReferences(referenceItems);
  };

  const save = async () => {
    try {
      await validate();
      const currentReferences = getCurrentReferences();
      const cachedReferences = cacheHandler.getReferences();

      const { toAdd, toRemove } = diffReferences(
        currentReferences,
        cachedReferences
      );

      if (toAdd.length === 0 && toRemove.length === 0) {
        return;
      }

      await handleRemoveReferences(toRemove);
      await handleAddReferences(toAdd);
    } catch (err: unknown) {
      if (!(err instanceof ValidationError)) {
        Sentry.captureException(err, {
          contexts: {
            params: {
              propertyId,
              referenceItems,
            },
          },
        });
      }

      throw err;
    }
  };

  return {
    referenceItems,
    add,
    edit,
    remove,
    save,
  };
};

export const diffReferences = (
  current: ExternalReference[],
  cached: ExternalReference[]
) => {
  const isEqual = (a: ExternalReference, b: ExternalReference) =>
    a.source.sourceEntityId === b.source.sourceEntityId &&
    a.source.sourceEntityType === b.source.sourceEntityType &&
    a.source.sourceType === b.source.sourceType;

  const toAdd = current.filter(c => {
    if (!c.source.sourceEntityId || !c.source.sourceType) {
      return false;
    }

    return !cached.some(i => isEqual(i, c));
  });
  const toRemove = cached.filter(i => !current.some(c => isEqual(i, c)));

  return { toAdd, toRemove };
};
